import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Check, Loader2, FolderKanban, Calendar, Calculator, FileText, Wrench } from 'lucide-react';

const ICONS = {
  'project-hub': FolderKanban,
  schedule: Calendar,
  'analysis-suite': Calculator,
  reports: FileText,
  'project-tools': Wrench,
};

export default function AddOnStore({ user, onUpdateUser }) {
  const [catalog, setCatalog] = useState([]);
  const [owned, setOwned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [cat, mine] = await Promise.all([
        apiService.getAddonCatalog(),
        apiService.getMyAddons(user.id),
      ]);
      setCatalog(cat.data.addons || []);
      setOwned(mine.data.owned || []);
    } catch (e) {
      toast.error('Could not load add-ons');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const status = params.get('status');
    const sessionId = params.get('session_id');
    if (status === 'success' && sessionId) {
      (async () => {
        try {
          const res = await apiService.addonConfirm({ user_id: user.id, session_id: sessionId });
          if (res.data.granted) {
            toast.success('Add-on unlocked!');
            const updated = { ...user, addons: res.data.owned };
            localStorage.setItem('user', JSON.stringify(updated));
            onUpdateUser?.(updated);
            setOwned(res.data.owned);
          } else {
            toast.message('Payment is still processing — refresh in a moment.');
          }
        } catch (e) {
          toast.error('Could not confirm your purchase');
        } finally {
          params.delete('status'); params.delete('session_id'); params.delete('addon');
          setParams(params, { replace: true });
        }
      })();
    } else if (status === 'cancel') {
      toast.message('Checkout canceled');
      params.delete('status'); setParams(params, { replace: true });
    }
  }, []);

  const buy = async (addonId) => {
    setBuying(addonId);
    try {
      const res = await apiService.addonCheckout({ user_id: user.id, addon_id: addonId });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout');
        setBuying(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setBuying(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="addon-store">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-600 mb-2">
          <Sparkles className="w-4 h-4" /> Add-ons store
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900">Power up DrawReady</h1>
        <p className="text-slate-500 mt-1">Keep your core workflow lean and switch on advanced modules only when you need them. $19/month each, cancel anytime.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {catalog.map((a) => {
            const Icon = ICONS[a.id] || Sparkles;
            const isOwned = owned.includes(a.id);
            return (
              <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col" data-testid={`addon-card-${a.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-extrabold text-slate-900">${a.price}</span>
                    <span className="text-slate-400 text-sm">/mo</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900">{a.name}</h3>
                <p className="text-sm text-slate-500 mt-1 flex-1">{a.description}</p>
                {isOwned ? (
                  <div className="mt-4 inline-flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 font-semibold px-4 py-2.5 rounded-xl border border-emerald-200" data-testid={`addon-owned-${a.id}`}>
                    <Check className="w-4 h-4" /> Active
                  </div>
                ) : (
                  <button
                    onClick={() => buy(a.id)}
                    disabled={buying === a.id}
                    data-testid={`addon-buy-${a.id}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors"
                  >
                    {buying === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {buying === a.id ? 'Redirecting…' : `Add for $${a.price}/mo`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
